import { useUser } from '@clerk/nextjs';
import { useEffect, useRef, useState } from 'react';
import { segmentClient } from '@/lib/analytics/segment';
import { generateRoomId } from '@/lib/client-utils';

type PendingCharacterSave = {
  name: string;
  descriptor: string;
  role: string;
  brawn: number;
  magic: number;
  wit: number;
  profile_picture_url: string;
  profile_picture_type?: string;
  creature: string;
  weapon: string;
  adornments: string;
};

function normalizePendingCharacterSave(data: any): PendingCharacterSave {
  const isLegacyShape = data?.magic == null && data?.charm != null;

  return {
    name: String(data?.name ?? ''),
    descriptor: String(data?.descriptor ?? ''),
    role: String(data?.role ?? ''),
    brawn: Number(data?.brawn ?? 0),
    magic: Number((isLegacyShape ? data?.wit : data?.magic) ?? 0),
    wit: Number((isLegacyShape ? data?.charm : data?.wit) ?? 0),
    profile_picture_url: String(data?.profile_picture_url ?? ''),
    profile_picture_type: data?.profile_picture_type
      ? String(data.profile_picture_type)
      : undefined,
    creature: String(data?.creature ?? ''),
    weapon: String(data?.weapon ?? ''),
    adornments: String(data?.adornments ?? ''),
  };
}

/**
 * Hook to handle saving pending character data after user authentication
 * This is used when a user signs in after creating a character anonymously
 */
export function usePendingCharacterSave() {
  const { isSignedIn, isLoaded } = useUser();
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  useEffect(() => {
    const handlePendingSave = async () => {
      // Wait for auth to load
      if (!isLoaded) {
        return;
      }

      // Check if user is signed in and has pending character data
      // Use ref to prevent infinite loops from isSaving state changes
      if (isSignedIn && !isSavingRef.current) {
        const pendingData = localStorage.getItem('pending_character_save');

        if (pendingData) {
          isSavingRef.current = true;
          setIsSaving(true);

          try {
            const characterData = normalizePendingCharacterSave(JSON.parse(pendingData));
            const anonymousId = await segmentClient.getAnonymousId();

            const response = await fetch('/api/save-character', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...characterData,
                anonymousId,
              }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error('Failed to save pending character:', errorData);
              throw new Error(errorData.error || 'Failed to save character');
            }

            await response.json();

            segmentClient.track('Button Pressed', {
              choiceValue: 'Character Saved After Auth',
              displayChoices: ['Character Saved After Auth'],
              eventCategory: 'character creation',
              eventSubCategory: 'character-creation',
            });

            // Clear pending data
            localStorage.removeItem('pending_character_save');

            // Redirect directly into a new Quest 1 room.
            // Use the same quest1 ID selection logic as the main game room client.
            const quest1Id = '9322446e-f5e5-45a0-bd47-a1111ad31041'; // Quest 1 ID

            const roomId = generateRoomId();
            const targetUrl = `/rooms/${roomId}?quest=${quest1Id}&characterExists=true`;

            // Use window.location for a hard redirect to ensure navigation happens
            // and isn't interrupted by LiveKit disconnect handlers
            window.location.href = targetUrl;
          } catch (err) {
            console.error('Error saving pending character:', err);
            // Don't clear localStorage so they can retry
            isSavingRef.current = false;
            setIsSaving(false);
          }
        }
      }
    };

    handlePendingSave();
    // Note: isSaving is intentionally NOT in the dependency array to prevent
    // infinite loops on save failure. This effect should only run when auth state changes.
  }, [isLoaded, isSignedIn]);

  return { isSaving };
}
